import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIcon, MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { IconPaletteType, IconType, LoadingService, ResponsiveService, SizeType } from '@cf/core';
import { EMPTY } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

@Component({
  selector: 'cf-icon',
  template: `
    <ng-template #iconTmp>
      <mat-icon
        [inline]="true"
        class="icon-color--{{ color }} icon-size--{{ size }}"
        [ngClass]="className"
        [svgIcon]="icon.toLowerCase()"
      ></mat-icon>
    </ng-template>

    <ng-container *ngIf="(loadingService.loadingState$ | async) && isLoadingIcon; else iconTmp">
      <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
    </ng-container>
  `,
  styleUrls: ['./icon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [MatIcon, NgClass, NgIf, AsyncPipe],
})
export class IconComponent implements OnInit, OnChanges {
  #elementRef = inject(ElementRef);
  @Input() icon: IconType = 'logo_blink';

  @Input() color: IconPaletteType = 'none';

  @Input() size: SizeType = 'md';

  @Input() touchSize!: SizeType;

  @Input() className!: string;

  @Input()
  @HostBinding('class.disabled')
  disabled = false;

  private destroyRef = inject(DestroyRef);

  constructor(
    public readonly loadingService: LoadingService,
    private readonly matIconRegistry: MatIconRegistry,
    private readonly sanitizer: DomSanitizer,
    private readonly responsiveService: ResponsiveService,
    private readonly cd: ChangeDetectorRef,
  ) {}

  private _isLoadingIcon = false;

  @Input()
  get isLoadingIcon(): boolean {
    return this._isLoadingIcon;
  }

  set isLoadingIcon(value: any) {
    this._isLoadingIcon = coerceBooleanProperty(value);
  }

  ngOnInit() {
    const initialSize = this.size;

    if (this.touchSize) {
      this.responsiveService.isTouch$.subscribe((isTouchScreen) => {
        if (isTouchScreen) {
          this.size = this.touchSize;
        } else {
          this.size = initialSize;
        }

        this.cd.detectChanges();
      });
    }

    this.loadIcon();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['icon'] && !changes['icon'].firstChange) {
      this.loadIcon();
    }
  }

  private loadIcon() {
    this.matIconRegistry
      .getNamedSvgIcon(this.icon)
      .pipe(
        catchError(() => {
          this.matIconRegistry.addSvgIcon(
            this.icon,
            this.sanitizer.bypassSecurityTrustResourceUrl(`assets/icons/${this.icon}.svg`),
          );
          this.delayedFix();
          return EMPTY;
        }),
        tap(() => this.delayedFix()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  delayedFix() {
    setTimeout(() => {
      this.fixIdsDuplicates();
    }, 100);
  }

  fixIdsDuplicates() {
    const svg = this.#elementRef.nativeElement.querySelector('svg');
    if (!svg) {
      return;
    }

    // Generate a unique suffix for this SVG instance
    const uniqueSuffix = `-${Math.random().toString(36).substring(2, 9)}`;

    // Get all elements with IDs in the SVG
    const elementsWithIds = svg.querySelectorAll('[id]');

    // Create a mapping of old IDs to new IDs
    const idMap: Record<string, string> = {};

    // First pass: create the mapping
    elementsWithIds.forEach((element: Element) => {
      const oldId = element.getAttribute('id');
      if (oldId) {
        const newId = `${oldId}${uniqueSuffix}`;
        idMap[oldId] = newId;
        // Update the id attribute
        element.setAttribute('id', newId);
      }
    });

    // Second pass: update all references to the IDs
    this.updateIdReferences(svg, idMap);
  }

  private updateIdReferences(element: Element, idMap: Record<string, string>) {
    // Handle all elements recursively
    const processElement = (el: Element) => {
      // Update attributes that might contain ID references
      const attributesToCheck = [
        'clip-path',
        'mask',
        'fill',
        'stroke',
        'filter',
        'marker-start',
        'marker-mid',
        'marker-end',
        // eslint-disable-next-line no-untranslated-string/no-untranslated-string
        'xlink:href',
        'href',
      ];

      attributesToCheck.forEach((attr) => {
        if (el.hasAttribute(attr)) {
          const value = el.getAttribute(attr);
          if (value && value.includes('#')) {
            // Handle url(#id) pattern
            let newValue = value;
            Object.keys(idMap).forEach((oldId) => {
              newValue = newValue.replace(`#${oldId}`, `#${idMap[oldId]}`);
            });
            el.setAttribute(attr, newValue);
            // eslint-disable-next-line no-untranslated-string/no-untranslated-string
          } else if ((attr === 'xlink:href' || attr === 'href') && value && value.startsWith('#')) {
            // Handle #id references
            const idRef = value.substring(1);
            if (idMap[idRef]) {
              el.setAttribute(attr, `#${idMap[idRef]}`);
            }
          }
        }
      });

      // Check for style attribute that might contain url references
      if (el.hasAttribute('style')) {
        const styleValue = el.getAttribute('style');
        if (styleValue && styleValue.includes('url(#')) {
          let newStyleValue = styleValue;
          Object.keys(idMap).forEach((oldId) => {
            newStyleValue = newStyleValue.replace(`url(#${oldId})`, `url(#${idMap[oldId]})`);
          });
          el.setAttribute('style', newStyleValue);
        }
      }

      // Process child elements
      Array.from(el.children).forEach((child) => {
        processElement(child as Element);
      });
    };

    // Start processing from the root element
    processElement(element);
  }
}
